/* eslint-disable max-lines-per-function */
import React from "react";
import { Form, Formik, FormikConfig } from "formik";
import { Grid, FormControlLabel, Checkbox, Typography } from "@mui/material";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { SubmitButton } from "components/haven-design/form";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { AdminFormLayout } from "../shared/admin-form";
import RichEditor from "./editor";

export type PlanForm = {
  name?: string;
  description?: string;
  isPublic?: boolean;
  defaultPricingId?: string;
  // Flattened per-frequency Invoiced plan ids; mapped to/from the nested input at the page edge.
  invoicedPlanIdAnnually?: string;
  invoicedPlanIdQuarterly?: string;
}

type Props = {
  initialData: PlanForm;
  formType: "update" | "new";
  handleSubmit: FormikConfig<PlanForm>["onSubmit"];
  sending: boolean;
  pricingOptions: Array<{ value: string; label: string }>;
}

const plansFormSchema = y.object().shape({
  __typename: y.string().strip(true),
  id: y.string().strip(true),

  name: y
    .string()
    .required("Plan name is required")
    .min(1, "Plan name cannot be empty")
    .max(100, "Plan name must be 100 characters or less"),

  description: y
    .string()
    .required("Plan description is required")
    .min(1, "Plan description cannot be empty")
    .max(500, "Plan description must be 500 characters or less"),


  isPublic: y.boolean().nullable(),

  defaultPricingId: y.string().nullable(),

  invoicedPlanIdAnnually: y.string().nullable(),
  invoicedPlanIdQuarterly: y.string().nullable(),
});

const PlanForm: React.FC<Props> = (props: Props) => (
  <AdminFormLayout>
    <Formik
      initialValues={props.initialData}
      onSubmit={props.handleSubmit}
      validationSchema={plansFormSchema}
      render={({ isSubmitting, values, setFieldValue }) => (
        <Form>
          <Grid container spacing={2} className="card" sx={{ mb: "30px" }}>
            <Typography>
              Create a subscription plan with pricing and feature
              configurations.
            </Typography>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input
                  margin="none"
                  label="Plan Name"
                  name="name"
                  required
                  placeholder="e.g., Professional Plan"
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <MaterialFormikSelectAutocomplete
                  id="defaultPricingId"
                  margin="none"
                  name="defaultPricingId"
                  placeholder="Default Pricing Configuration"
                  title="Default Pricing Configuration"
                  options={props.pricingOptions}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <RichEditor name="description" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input
                  margin="none"
                  label="Invoiced Plan ID (Annual)"
                  name="invoicedPlanIdAnnually"
                  placeholder="e.g., pro-annual"
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input
                  margin="none"
                  label="Invoiced Plan ID (Quarterly)"
                  name="invoicedPlanIdQuarterly"
                  placeholder="e.g., pro-quarterly"
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isPublic || false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("isPublic", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Make this plan publicly visible"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <HistoryBackButton marginRight size="L" />
            <SubmitButton
              isWide
              text="Submit"
              loadingText="Sending"
              isLoading={isSubmitting}
            />
          </Grid>
        </Form>
      )}
    />
  </AdminFormLayout>
);

export default PlanForm;
